import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { Button, Icon } from '@rocketlane/ui';
import { useGetDocumentationAgentQuery } from 'queries/document-agent';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  AgentDataCollectionForm,
  type AgentFormData,
} from 'components/organisms/agent-data-collection-form';

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 32px;

  h1 {
    margin: 0;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: var(--spacing-03);
`;

const LayoutContainer = styled.div`
  display: flex;
  height: 100%;
  gap: var(--spacing-03);
`;

const FormPane = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
  padding: var(--spacing-06);
  border-radius: var(--border-radius-small);
  background-color: white;
  overflow-y: auto;
  height: 95%;
  align-items: center;
`;

export const DocumentAgentTestPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { documentationAgentId } = useParams();

  const { prompt, color, companyId } =
    (location.state as {
      prompt: any;
      color: any;
      companyId?: number;
    }) || {};

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch agent data
  const { data: agentData, isLoading: isAgentLoading } =
    useGetDocumentationAgentQuery(Number(documentationAgentId), {
      enabled: !!documentationAgentId,
    });

  const handleFormSubmit = async (data: AgentFormData) => {
    setIsSubmitting(true);
    try {
      // Empty function as requested - just log
      // eslint-disable-next-line no-console
      console.log('Form submitted with data:', data);
      // TODO: Implement actual test agent logic here
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/agent-builder/documentation/${documentationAgentId}/preview`, {
      state: { prompt, color, documentationAgentId },
    });
  };

  const showUserInputForm =
    agentData && agentData.configuration.sourceType === 'USER_INPUT';

  const hasAgentInputs =
    agentData && agentData.input_fields && agentData.input_fields.length > 0;

  return (
    <ContentContainer>
      <Header>
        <Button
          variant="ghost"
          leftIcon={<Icon icon={faArrowLeft} size=".75x" />}
          onClick={() => {
            navigate(
              `/agent-builder/documentation/${documentationAgentId}/preview`,
              {
                state: { prompt, color, documentationAgentId },
              },
            );
          }}
        />
        <h1 className="heading-compact-01">
          {prompt?.agentName || 'Test Agent'}
        </h1>
        <div>
          <Button
            variant="primary"
            onClick={() => {
              navigate(
                `/agent-builder/documentation/${documentationAgentId}/preview`,
                {
                  state: { prompt, color, documentationAgentId },
                },
              );
            }}
          >
            Exit Testing
          </Button>
        </div>
      </Header>
      <LayoutContainer>
        <FormPane>
          {isAgentLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              Loading agent data...
            </div>
          ) : !agentData ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              No agent data available
            </div>
          ) : (
            <>
              {(showUserInputForm || hasAgentInputs) && (
                <AgentDataCollectionForm
                  agentData={agentData}
                  companyId={companyId || 0}
                  onSubmit={handleFormSubmit}
                  onCancel={handleCancel}
                  isSubmitting={isSubmitting}
                />
              )}

              {!showUserInputForm && !hasAgentInputs && (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  No configuration required for this agent
                </div>
              )}
            </>
          )}
        </FormPane>
      </LayoutContainer>
    </ContentContainer>
  );
};
